import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/ui/button/Button';
import { Loader } from '@/components/ui/loader/Loader';
import { useArchivePolling, ArchiveStatus } from '../hooks/useArchivePolling';

interface ArchiveVerificationStatusProps {
  clientId: string;
  verificationStatus: string;
}

export const ArchiveVerificationStatus: React.FC<ArchiveVerificationStatusProps> = ({
  clientId,
  verificationStatus,
}) => {
  const { t } = useTranslation();
  const {
    archiveState,
    downloadArchive,
    acceptVerification,
    declineVerification,
    isAccepting,
    isDeclining,
  } = useArchivePolling(clientId, verificationStatus);

  // Don't show anything if verification is not needed or completed
  if (verificationStatus !== 'verification_needed' || archiveState.status === 'completed') {
    return null;
  }

  // Don't show anything while in idle state (initial check is happening)
  // This prevents showing loader when archive might already be available
  if (archiveState.status === 'idle') {
    return null;
  }

  const formatTimeRemaining = (milliseconds: number): string => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const renderStatusContent = () => {
    switch (archiveState.status) {

      case 'waiting':
        return (
          <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Loader size="sm" />
            <div>
              <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {t('clients.archive.waiting_for_documents')}
              </p>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                {t('clients.archive.checking_every_5_seconds')}
              </p>
            </div>
          </div>
        );

      case 'available':
        return (
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-900 dark:text-green-100">
                  {t('clients.archive.documents_ready')}
                </p>
                <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                  {t('clients.archive.click_to_download')}
                </p>
              </div>
              <Button
                variant="primary"
                size="sm"
                onClick={downloadArchive}
              >
                {t('clients.archive.get_archive')}
              </Button>
            </div>
          </div>
        );

      case 'reviewing':
        return (
          <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
              <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                {t('clients.archive.reviewing_documents')}
              </p>
            </div>

            {archiveState.timeRemaining && (
              <div className="mb-3">
                <p className="text-xs text-orange-700 dark:text-orange-300">
                  {t('clients.archive.time_remaining')}: {formatTimeRemaining(archiveState.timeRemaining)}
                </p>
                <div className="w-full bg-orange-200 dark:bg-orange-800 rounded-full h-2 mt-2">
                  <div
                    className="bg-orange-500 h-2 rounded-full transition-all duration-1000"
                    style={{
                      width: `${((5 * 60 * 1000 - archiveState.timeRemaining) / (5 * 60 * 1000)) * 100}%`
                    }}
                  ></div>
                </div>
              </div>
            )}

            <div className="text-xs text-orange-700 dark:text-orange-300 space-y-1">
              <p>{t('clients.archive.review_instructions.title')}</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>{t('clients.archive.review_instructions.step1')}</li>
                <li>{t('clients.archive.review_instructions.step2')}</li>
                <li>{t('clients.archive.review_instructions.step3')}</li>
              </ul>
            </div>
          </div>
        );

      case 'ready_to_verify':
        return (
          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <div className="mb-3">
              <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                {t('clients.archive.ready_to_decide')}
              </p>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                {t('clients.archive.make_decision')}
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="danger"
                size="sm"
                onClick={declineVerification}
                disabled={isDeclining || isAccepting}
              >
                {isDeclining ? (
                  <div className="flex items-center gap-2">
                    <Loader size="sm" />
                    {t('clients.archive.declining')}
                  </div>
                ) : (
                  t('clients.archive.decline')
                )}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={acceptVerification}
                disabled={isAccepting || isDeclining}
              >
                {isAccepting ? (
                  <div className="flex items-center gap-2">
                    <Loader size="sm" />
                    {t('clients.archive.accepting')}
                  </div>
                ) : (
                  t('clients.archive.accept')
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="mt-4">
      {renderStatusContent()}

      {archiveState.error && (
        <div className="mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-xs text-red-700 dark:text-red-300">
            {archiveState.error}
          </p>
        </div>
      )}
    </div>
  );
};
